import { NgModule } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { Routes, RouterModule, PreloadAllModules } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SidenavComponent } from './core/components/sidenav/sidenav.component';
import { AuthGuard } from './core/services/auth.guard';
import { RoleRightGuard } from './core/services/role-right.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadChildren: () =>
      import('./pages/login-page/login-page.module').then(
        (m) => m.LoginPageModule,
      ),
  },
  {
    path: 'passwordreset',
    loadChildren: () =>
      import('./pages/password-reset-page/password-reset-page.module').then(
        (m) => m.PasswordResetPageModule,
      ),
  },
  {
    path: 'customerinfo',
    loadChildren: () =>
      import('./pages/customer-info-record/customer-info-record.module').then(
        (m) => m.CustomerInfoRecordModule,
      ),
  },
  {
    path: 'portal',
    component: SidenavComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./pages/dashboard/dashboard.module').then(
            (m) => m.DashboardModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'partnerlist',
        loadChildren: () =>
          import('./pages/partner-list-page/partner-list-page.module').then(
            (m) => m.PartnerListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Partner_List_Partners'],
        },
      },
      {
        path: 'documentPrefix',
        loadChildren: () =>
          import('./pages/organization-pages/document-prefix-pages/document-prefix-pages.module').then(
            (m) => m.DocumentPrefixPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['DocumentPrefix_List_DocumentPrefix'],
        },
      },
      {
        path: 'documentXslt',
        loadChildren: () =>
          import('./pages/organization-pages/document-xslt-pages/document-xslt-pages.module').then(
            (m) => m.DocumentXsltPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['XsltDocument_List_XsltDocument'],
        },
      },
      {
        path: 'documentMapping',
        loadChildren: () =>
          import('./pages/organization-pages/document-mapping-pages/document-mapping-pages.module').then(
            (m) => m.DocumentMappingPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['DocumentMapping_List_DocumentMapping'],
        },
      },
      {
        path: 'ruleDefinition',
        loadChildren: () =>
          import('./pages/organization-pages/rule-definition-pages/rule-definition-pages.module').then(
            (m) => m.RuleDefinitionPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Rules_List_RuleDefinitions'],
        },
      },
      {
        path: 'customerRuleDefinition',
        loadChildren: () =>
          import('./pages/organization-pages/customer-rule-definition-pages/customer-rule-definition-pages.module').then(
            (m) => m.CustomerRuleDefinitionPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Rules_List_CustomerRules'],
        },
      },
      {
        path: 'additionalField',
        loadChildren: () =>
          import('./pages/organization-pages/additional-field-definition-pages/additional-field-definition-pages.module').then(
            (m) => m.AdditionalFieldDefinitionPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['OutgoingAdditionalFields_List_Fields'],
        },
      },
      {
        path: 'ipFilterManagement',
        loadChildren: () =>
          import('./pages/organization-pages/customer-ip-filter-pages/customer-ip-filter-pages.module').then(
            (m) => m.CustomerIpFilterPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['CustomerAllowedIP_List_Filters'],
        },
      },

      {
        path: 'eledgerArchiveServerDefinition',
        loadChildren: () =>
          import('./pages/organization-pages/eledger-archive-definition-pages/eledger-archive-definition-pages.module').then(
            (m) => m.EledgerArchiveDefinitionPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['ELedgerGibAppServer_List_Servers'],
        },
      },
      {
        path: 'eledgerArchiveCustomerServer',
        loadChildren: () =>
          import('./pages/organization-pages/eledger-archive-customer-server-pages/eledger-archive-customer-server-pages.module').then(
            (m) => m.EledgerArchiveCustomerServerPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['ELedgerGibAppServer_List_Servers'],
        },
      },
      {
        path: 'apiPathDefinition',
        loadChildren: () =>
          import('./pages/organization-pages/api-path-definition-pages/api-path-definition-pages.module').then(
            (m) => m.ApiPathDefinitionPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['ApiLocalization_List_Paths'],
        },
      },
      {
        path: 'customerApiLanguage',
        loadChildren: () =>
          import('./pages/organization-pages/customer-api-language-pages/customer-api-language-pages.module').then(
            (m) => m.CustomerApiLanguagePagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['ApiLocalization_List_CustomerLanguages'],
        },
      },
      {
        path: 'apiTextDefinition',
        loadChildren: () =>
          import('./pages/organization-pages/api-text-definition-pages/api-text-definition-pages.module').then(
            (m) => m.ApiTextDefinitionPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['ApiLocalization_List_ApiTextValue'],
        },
      },
      {
        path: 'customerFtpDefinition',
        loadChildren: () =>
          import('./pages/customer-ftp-definition-page/customer-ftp-definition-page.module').then(
            (m) => m.CustomerFtpDefinitionPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['CustomerSftpInfoDefin_List_Ftp'],
        },
      },

      {
        path: 'systemControl',
        loadChildren: () =>
          import('./pages/organization-pages/system-control-pages/system-control-pages.module').then(
            (m) => m.SystemControlPagesModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['SystemDocumentFlow_GibConnectorList'],
        },
      },
      {
        path: 'customerchange',
        loadChildren: () =>
          import('./pages/empty-page/empty-page.module').then(
            (m) => m.EmptyPageModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'customerlist',
        loadChildren: () =>
          import('./pages/customer-list-page/customer-list-page.module').then(
            (m) => m.CustomerListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Customer_List_Customers'],
        },
      },
      {
        path: 'rolelist',
        loadChildren: () =>
          import('./pages/role-list-page/role-list-page.module').then(
            (m) => m.RoleListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Role_List_Roles'],
        },
      },
      {
        path: 'roleUpdate',
        loadChildren: () =>
          import('./pages/role-create-update/role-create-update.module').then(
            (m) => m.RoleCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Role_Update_Role'],
        },
      },
      {
        path: 'roleCreate',
        loadChildren: () =>
          import('./pages/role-create-update/role-create-update.module').then(
            (m) => m.RoleCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Role_Add_Role'],
        },
      },
      {
        path: 'parameterlist',
        loadChildren: () =>
          import('./pages/parameter-list-page/parameter-list-page.module').then(
            (m) => m.ParameterListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['AppPrm_List_Parameters'],
        },
      },
      {
        path: 'parameterUpdate',
        loadChildren: () =>
          import('./pages/parameter-update/parameter-update.module').then(
            (m) => m.ParameterUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['AppPrm_Update_Parameter'],
        },
      },
      {
        path: 'agreementServiceCoefficient',
        loadChildren: () =>
          import('./pages/agreement-service-coef-list-page/agreement-service-coef-list-page.module').then(
            (m) => m.AgreementServiceCoefListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Agreements_List_AgreementServiceCoef'],
        },
      },
      {
        path: 'partnerAgreements',
        loadChildren: () =>
          import('./pages/partner-agreement-list-page/partner-agreement-list-page.module').then(
            (m) => m.PartnerAgreementListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Agreements_List_PartnerAgreements'],
        },
      },
      {
        path: 'partnerAgreementsCreate',
        loadChildren: () =>
          import('./pages/partner-agreement-create-update/partner-agreement-create-update.module').then(
            (m) => m.PartnerAgreementCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Agreements_Add_PartnerAgreement'],
        },
      },
      {
        path: 'partnerAgreementsUpdate',
        loadChildren: () =>
          import('./pages/partner-agreement-create-update/partner-agreement-create-update.module').then(
            (m) => m.PartnerAgreementCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Agreements_Update_PartnerAgreement'],
        },
      },
      {
        path: 'customerAgreements',
        loadChildren: () =>
          import('./pages/customer-agreement-list-page/customer-agreement-list-page.module').then(
            (m) => m.CustomerAgreementListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Agreements_List_CustomerAgreements'],
        },
      },
      {
        path: 'customerAgreementsUpdate',
        loadChildren: () =>
          import('./pages/customer-agreement-create-update/customer-agreement-create-update.module').then(
            (m) => m.CustomerAgreementCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Agreements_Update_CustomerAgreement'],
        },
      },
      {
        path: 'customerAgreementsCreate',
        loadChildren: () =>
          import('./pages/customer-agreement-create-update/customer-agreement-create-update.module').then(
            (m) => m.CustomerAgreementCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Agreements_Add_CustomerAgreement'],
        },
      },
      {
        path: 'partnerContourList',
        loadChildren: () =>
          import('./pages/partner-contour-list-page/partner-contour-list-page.module').then(
            (m) => m.PartnerContourListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Contours_List_PartnerContourDocuments'],
        },
      },
      {
        path: 'partnerContourCreate',
        loadChildren: () =>
          import('./pages/partner-contour-create-update/partner-contour-create-update.module').then(
            (m) => m.PartnerContourCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Contours_Add_PartnerContourDocuments'],
        },
      },
      {
        path: 'partnerContourUpdate',
        loadChildren: () =>
          import('./pages/partner-contour-create-update/partner-contour-create-update.module').then(
            (m) => m.PartnerContourCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Contours_Update_PartnerContourDocuments'],
        },
      },
      {
        path: 'customerContourList',
        loadChildren: () =>
          import('./pages/customer-contour-list-page/customer-contour-list-page.module').then(
            (m) => m.CustomerContourListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Contours_List_CustomerContourDocuments'],
        },
      },
      {
        path: 'customerContourCreate',
        loadChildren: () =>
          import('./pages/customer-contour-create-update/customer-contour-create-update.module').then(
            (m) => m.CustomerContourCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Contours_Add_CustomerContourDocuments'],
        },
      },
      {
        path: 'customerContourUpdate',
        loadChildren: () =>
          import('./pages/customer-contour-create-update/customer-contour-create-update.module').then(
            (m) => m.CustomerContourCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Contours_Update_CustomerContourDocuments'],
        },
      },
      {
        path: 'customerActivisionList',
        loadChildren: () =>
          import('./pages/activison-list-page/activison-list-page.module').then(
            (m) => m.ActivisonListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Activations_List_Activations'],
        },
      },
      {
        path: 'serviceActivisionCreate',
        loadChildren: () =>
          import('./pages/service-activision-create/service-activision-create.module').then(
            (m) => m.ServiceActivisionCreateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Activations_Create_Activation'],
        },
      },
      {
        path: 'partnerRouteTenantList',
        loadChildren: () =>
          import('./pages/partner-route-tenant-list/partner-route-tenant-list.module').then(
            (m) => m.PartnerRouteTenantListModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['PartnerRouteTenants_List_RouteTenants'],
        },
      },
      {
        path: 'userlist',
        loadChildren: () =>
          import('./pages/user-list-page/user-list-page.module').then(
            (m) => m.UserListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['PartnerUser_List_Users'],
        },
      },
      {
        path: 'userCreate',
        loadChildren: () =>
          import('./pages/user-create-update/user-create-update.module').then(
            (m) => m.UserCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['PartnerUser_Add_User'],
        },
      },
      {
        path: 'userUpdate',
        loadChildren: () =>
          import('./pages/user-create-update/user-create-update.module').then(
            (m) => m.UserCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['PartnerUser_Update_User'],
        },
      },
      {
        path: 'customerBranchlist',
        loadChildren: () =>
          import('./pages/customer-branch-list-page/customer-branch-list-page.module').then(
            (m) => m.CustomerBranchListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['CustomerBranch_List_Branch'],
        },
      },
      {
        path: 'customerBranchUpdate',
        loadChildren: () =>
          import('./pages/customer-branch-create-update/customer-branch-create-update.module').then(
            (m) => m.CustomerBranchCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['CustomerBranch_Update_Branch'],
        },
      },
      {
        path: 'customerBranchCreate',
        loadChildren: () =>
          import('./pages/customer-branch-create-update/customer-branch-create-update.module').then(
            (m) => m.CustomerBranchCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['CustomerBranch_Add_Branch'],
        },
      },
      {
        path: 'customerBranchUserlist',
        loadChildren: () =>
          import('./pages/customer-branch-user-list-page/customer-branch-user-list-page.module').then(
            (m) => m.CustomerBranchUserListPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['CustomerBranchUsers_List_BranchUsers'],
        },
      },
      {
        path: 'partnerCreate',
        loadChildren: () =>
          import('./pages/partner-create-update/partner-create-update.module').then(
            (m) => m.PartnerCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Partner_Add_Partner'],
        },
      },
      {
        path: 'partnerUpdate',
        loadChildren: () =>
          import('./pages/partner-create-update/partner-create-update.module').then(
            (m) => m.PartnerCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Partner_Update_Partner'],
        },
      },
      {
        path: 'customerCreate',
        loadChildren: () =>
          import('./pages/customer-create-update/customer-create-update.module').then(
            (m) => m.CustomerCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Customer_Add_Customer'],
        },
      },
      {
        path: 'customerUpdate',
        loadChildren: () =>
          import('./pages/customer-create-update/customer-create-update.module').then(
            (m) => m.CustomerCreateUpdateModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Customer_Update_Customer'],
        },
      },
      {
        path: 'ledgerArchiveReport',
        loadChildren: () =>
          import('./pages/report-pages/ledger-archive-report-page/ledger-archive-report-page.module').then(
            (m) => m.LedgerArchiveReportPageModule,
          ),
        canActivate: [AuthGuard, RoleRightGuard],
        data: {
          allowedRights: ['Partner_Get_Ledger_Archive_Report'],
        },
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'login',
    pathMatch: 'full',
  },
];

@NgModule({
  //preloadingStrategy: PreloadAllModules butun componentlerın bır anda yuklenmesını saglar. Onerılen bu degıldır cunku bızım yapımız lazyloading.Denenmesı ıcın koyuldu
  // imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }), TranslateModule],
  imports: [RouterModule.forRoot(routes), TranslateModule],

  exports: [RouterModule],
})
export class AppRoutingModule {}
